import React, { useEffect, useMemo, useState, useRef } from "react";
import './Material.css';
import { getMaterialUrl } from '../../config/api';
import UniversalPagination from '../UniversalPagination';
import MaterialFilter from '../Filters/MaterialFilter';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MaterialHistoryPdfData } from '../../utils/materialsPdfUtils';

interface MaterialDataPE {
    id?: number;
    index: number;
    date: string;
    HD03580SB: string | null;
    HD10530LB: string | null;
    plast02651: string | null;
    HD03490NP: string | null;
    stavrolen27383: string | null;
    HD03594PE: string | null;
    HM0349PE: string | null;
    HM0359PE: string | null;
    PE6PP32: string | null;
    p2nt119: string | null;
    HD12500LB: string | null;
    plast27673: string | null;
    HD07580SB: string | null;
    PE100RC: string | null;
}

interface MaterialDataPPR {
    id?: number;
    index: number;
    date: string;
    PPI013GP5: string | null;
    PPG200224: string | null;
    PPH250GP2: string | null;
    plast1030: string | null;
    PPG103508: string | null;
    PPH030GPTomskneftehim: string | null;
    PPH030GPNeftehimia: string | null;
    PPH030GPPolium: string | null;
    PPH030GPZapSibNeftehim: string | null;
    SIBEX_PP_R003_EX1: string | null;
}

interface MaterialDataPVH {
    id?: number;
    index: number;
    date: string;
    C70Y: string | null;
    PC271: string | null;
    SI70: string | null;
    C5868PG: string | null;
    C6359M: string | null;
    C6669PG: string | null;
    RC267: string | null;
    C6669GC: string | null;
    SI67: string | null;
    C7059M: string | null;
}

type MaterialData = MaterialDataPE | MaterialDataPPR | MaterialDataPVH;

const deduplicateData = (data: MaterialData[]): MaterialData[] => {
    const seen = new Set<string>();
    
    return data.filter((item) => {
        const itemWithoutId = { ...item };
        delete (itemWithoutId as any).id;
        delete (itemWithoutId as any).index;
        
        const key = `${item.date}-${JSON.stringify(itemWithoutId)}`;
        
        if (seen.has(key)) {
            return false;
        }
        
        seen.add(key);
        return true;
    });
};

const HISTORY_COLUMNS: Record<'pe' | 'ppr' | 'pvh', { key: string; label: string }[]> = {
    pe: [
        { key: 'HD03580SB', label: 'HD 03580 SB' },
        { key: 'HD10530LB', label: 'HD 10530 LB' },
        { key: 'plast02651', label: '0,26/51' },
        { key: 'HD03490NP', label: 'HD 03490 NP' },
        { key: 'stavrolen27383', label: '273-83 Ставролен' },
        { key: 'HD03594PE', label: 'HD 03594 PE' },
        { key: 'HM0349PE', label: 'HM 0349 PE' },
        { key: 'HM0359PE', label: 'HM 0359 PE' },
        { key: 'PE6PP32', label: 'PE6PP32' },
        { key: 'p2nt119', label: 'ПЭ2НТ11-9' },
        { key: 'HD12500LB', label: 'HD 12500 LB' },
        { key: 'plast27673', label: '276-73' },
        { key: 'HD07580SB', label: 'HD 07580 SB' },
        { key: 'PE100RC', label: 'PE 100RC (синий)' },
    ],
    ppr: [
        { key: 'PPI013GP5', label: 'PPI 013 GP5' },
        { key: 'PPG200224', label: 'PPG 200224' },
        { key: 'PPH250GP2', label: 'PPH 250 GP2' },
        { key: 'plast1030', label: '0,30/10' },
        { key: 'PPG103508', label: 'PPG 103508' },
        { key: 'PPH030GPTomskneftehim', label: 'PPH 030 GP ТНХ' },
        { key: 'PPH030GPNeftehimia', label: 'PPH 030 GP Нефтехимия' },
        { key: 'PPH030GPPolium', label: 'PPH 030 GP Полиум' },
        { key: 'PPH030GPZapSibNeftehim', label: 'PPH 030 GP ЗСНХ' },
        { key: 'SIBEX_PP_R003_EX1', label: 'SIBEX PP R003 EX1' },
    ],
    pvh: [
        { key: 'C70Y', label: 'C70Y' },
        { key: 'PC271', label: 'PC271' },
        { key: 'SI70', label: 'SI70' },
        { key: 'C5868PG', label: 'C5868PG' },
        { key: 'C6359M', label: 'C6359M' },
        { key: 'C6669PG', label: 'C6669PG' },
        { key: 'RC267', label: 'RC267' },
        { key: 'C6669GC', label: 'C6669GC' },
        { key: 'SI67', label: 'SI67' },
        { key: 'C7059M', label: 'C7059M' },
    ],
};

const formatHistoryDate = (date: string) => new Date(date).toLocaleDateString('ru-RU');
const getHistoryValue = (item: MaterialData, key: string) => String((item as any)[key] || '-');

interface MaterialRequestProps {
    materialType?: string;
    onPdfDataChange?: (data: MaterialHistoryPdfData) => void;
}

function MaterialRequest({ materialType, onPdfDataChange }: MaterialRequestProps) {
    const [materialData, setMaterialData] = useState<MaterialData[]>([]);
    const [filteredData, setFilteredData] = useState<MaterialData[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' | null }>({ key: '', direction: null });
    const [originalData, setOriginalData] = useState<MaterialData[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [appliedPeriod, setAppliedPeriod] = useState('');

    const fileName = useMemo(() => {
        const map: Record<string, string> = { pe: 'polyethylene', ppr: 'polypropylene', pvh: 'pvc' };
        return `materials_history_${map[materialType || 'pe']}_${new Date().toISOString().slice(0,10)}.xlsx`;
    }, [materialType]);

    useEffect(() => {
        const fetchMaterialData = async () => {
            if (!materialType) {
                setError('Material type is required');
                setIsLoading(false);
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            try {
                setIsLoading(true);
                const access = localStorage.getItem('access');
                const response = await fetch(getMaterialUrl(materialType), {
                    headers: access ? { 'Authorization': `Bearer ${access}` } : {},
                    signal: abortControllerRef.current.signal
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (!Array.isArray(data)) {
                    throw new Error('Expected array data');
                }

                const uniqueData = deduplicateData(data);

                const sortedByDateDesc = [...uniqueData].sort((a: MaterialData, b: MaterialData) => {
                    const da = new Date((a as any).date).getTime();
                    const db = new Date((b as any).date).getTime();
                    return db - da;
                });

                setMaterialData(sortedByDateDesc);
                setOriginalData(sortedByDateDesc);
                setError('');
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }
                console.error('Error fetching material history data:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchMaterialData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [materialType]);

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' | null = 'asc';

        if (sortConfig.key === key) {
            if (sortConfig.direction === 'asc') {
                direction = 'desc';
            } else if (sortConfig.direction === 'desc') {
                direction = null;
            }
        }

        setSortConfig({ key, direction });

        if (direction === null) {
            setMaterialData([...originalData]);
            return;
        }

        const sortedData = [...materialData].sort((a, b) => {
            const aValue = a[key as keyof MaterialData];
            const bValue = b[key as keyof MaterialData];

            if (aValue === null || aValue === '-' || aValue === undefined) return direction === 'asc' ? 1 : -1;
            if (bValue === null || bValue === '-' || bValue === undefined) return direction === 'asc' ? -1 : 1;

            if (direction === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setMaterialData(sortedData);
    };

    const getSortIcon = (key: string) => {
        if (sortConfig.key !== key) return '↕';
        if (sortConfig.direction === 'asc') return '↑';
        if (sortConfig.direction === 'desc') return '↓';
        return '↕';
    };

    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const paginate = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleFilterChange = (startDate: string, endDate: string) => {
        setAppliedPeriod(startDate && endDate ? `с ${formatHistoryDate(startDate)} по ${formatHistoryDate(endDate)}` : '');
        if (!startDate || !endDate) {
            const uniqueOriginalData = deduplicateData(originalData);
            setFilteredData(uniqueOriginalData);
            return;
        }

        const filtered = originalData.filter(item => {
            const itemDate = new Date(item.date);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return itemDate >= start && itemDate <= end;
        });

        const uniqueFiltered = deduplicateData(filtered);

        setFilteredData(uniqueFiltered);
        setCurrentPage(1);
    };

    useEffect(() => {
        const uniqueFilteredData = deduplicateData(materialData);
        setFilteredData(uniqueFilteredData);
        setCurrentPage(1);
    }, [materialData, materialType]);

    useEffect(() => {
        if (!onPdfDataChange || !materialType) return;
        const columns = HISTORY_COLUMNS[materialType as keyof typeof HISTORY_COLUMNS] || HISTORY_COLUMNS.pe;
        onPdfDataChange({
            materialType,
            period: appliedPeriod,
            columns: ['Дата записи', ...columns.map(column => column.label)],
            rows: filteredData.map(item => [formatHistoryDate(item.date), ...columns.map(column => getHistoryValue(item, column.key))]),
        });
    }, [filteredData, materialType, appliedPeriod, onPdfDataChange]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    
    const finalItems = deduplicateData(currentItems);

    const handleExportExcel = () => {
        if (!filteredData.length) return;

        const rows = filteredData.map((item) => ({
            Дата: new Date(item.date).toLocaleDateString('ru-RU'),
            ...Object.fromEntries(
                Object.entries(item)
                    .filter(([key]) => key !== 'date' && key !== 'index')
                    .map(([key, value]) => [key, value ?? '-'])
            )
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'История');
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, fileName);
    };

    if (isLoading) {
        return <div className="loading-message">Загрузка...</div>;
    }

    if (error) {
        return <div className="error-message">Ошибка: {error}</div>;
    }

    if (!materialData.length) {
        return <div className="no-data">Нет данных</div>;
    }

    const historyColumns = HISTORY_COLUMNS[(materialType as keyof typeof HISTORY_COLUMNS)] || HISTORY_COLUMNS.pe;

    const renderTable = () => (
        <table className="material-table">
            <thead className="material-thead">
                <tr>
                    <th onClick={() => handleSort('date')}>Дата записи {getSortIcon('date')}</th>
                    {historyColumns.map(column => (
                        <th key={column.key} onClick={() => handleSort(column.key)}>{column.label} {getSortIcon(column.key)}</th>
                    ))}
                </tr>
            </thead>
            <tbody className="material-tbody">
                {finalItems.map((item, index) => (
                    <tr key={`${materialType}-${item.date}-${index}-${item.id || index}`} className="material-row">
                        <td>{formatHistoryDate(item.date)}</td>
                        {historyColumns.map(column => <td key={column.key}>{getHistoryValue(item, column.key)}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="material-container">
            <div className="material-header">
                <h3>История</h3>
            </div>
            <MaterialFilter 
                onFilterChange={handleFilterChange}
                exportButton={<button className="export-btn" onClick={handleExportExcel}>Экспорт в Excel</button>}
            />
            <div className="material-controls">
                <div className="items-per-page">
                    <label htmlFor="itemsPerPage">Количество на странице:</label>
                    <select
                        id="itemsPerPage"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={filteredData.length}>Все</option>
                    </select>
                </div>
            </div>
            <div className="material-table-container">
                {renderTable()}
            </div>
            <UniversalPagination
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                paginate={paginate}
                items={filteredData}
            />
        </div>
    )
}

export default MaterialRequest;